"use client";
import { useEffect, useMemo, useState } from "react";
import { filter as lodashFilter, orderBy } from "lodash";
import LinkService from "@/app/_services/LinkService";
import { toast } from "@/app/_utils/toast";
import { useLinkTaxonomy } from "./_cmp/linkForm/useLinkTaxonomy";

export type LinkSignupGuest = {
  id: string;
  index: number | null;
  first_name: string;
  last_name: string | null;
  email: string | null;
  instagram: string | null;
  subscribed: boolean;
  signup_date: string;
  // Guest.signups is a JSONField — array, string or null depending on the row.
  signups: unknown;
  // Annotation-only on the backend; absent from the link-signups payload today.
  num_shows?: number;
};

export type LinkSignups = { new: number; repeat: number; guests: LinkSignupGuest[] };

export type Link = {
  id: string;
  name: string;
  redirect: string;
  visits: number;
  medium: string | null;
  source: string | null;
  // Share-card overrides; blank falls back to the linked show, then the site
  // default. Resolution happens server-side — see LinkPreview.
  preview_title: string | null;
  preview_description: string | null;
  preview_img_url: string | null;
  created_at: string;
  updated_at: string;
  signups: LinkSignups;
};

/** Server-resolved share card, from GET links/<id>/preview/. */
export type LinkPreview = {
  title: string;
  description: string;
  image: string;
  source: "custom" | "show" | "default";
};

export function shortLinkUrl(link: Pick<Link, "id">) {
  return `${window.location.origin}/links/${link.id}`;
}

export function useLinksModel() {
  const [links, setLinks] = useState<Link[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [mediumFilter, setMediumFilter] = useState("all");
  const [openForm, setOpenForm] = useState(false);

  const { mediums, sources } = useLinkTaxonomy();

  async function loadLinks() {
    setIsLoading(true);
    try {
      const res = await LinkService.getLinksAsync();
      setLinks(orderBy(res, ["created_at"], ["desc"]));
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadLinks();
  }, []);

  const filteredLinks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lodashFilter(links, (l) => {
      const matchesSearch =
        !q ||
        l.name?.toLowerCase().includes(q) ||
        l.redirect?.toLowerCase().includes(q);
      const matchesSource = sourceFilter === "all" || l.source === sourceFilter;
      const matchesMedium = mediumFilter === "all" || l.medium === mediumFilter;
      return matchesSearch && matchesSource && matchesMedium;
    });
  }, [links, search, sourceFilter, mediumFilter]);

  function openCreateForm() {
    setOpenForm(true);
  }

  function closeForm() {
    setOpenForm(false);
  }

  async function handleDelete(link: Link) {
    try {
      const res = await LinkService.deleteLinkAsync(link.id);
      if (!res.ok) throw new Error(`Delete failed with ${res.status}`);
      toast.success("Link deleted");
      await loadLinks();
    } catch (e) {
      toast.error("Failed to delete link");
    }
  }

  async function copyLinkUrl(link: Link) {
    try {
      await navigator.clipboard.writeText(shortLinkUrl(link));
      toast.success("Link copied");
    } catch (e) {
      toast.error("Failed to copy link");
    }
  }

  return {
    filteredLinks,
    isLoading,
    search,
    setSearch,
    sourceFilter,
    setSourceFilter,
    mediumFilter,
    setMediumFilter,
    mediums,
    sources,
    openForm,
    openCreateForm,
    closeForm,
    handleDelete,
    copyLinkUrl,
    refetch: loadLinks,
  };
}
